import {useMessages} from "i18n";
import {ProPill} from "home/components/UpgradeHints";
import {ConfirmDialog, ConfirmBody} from "components/confirm/ConfirmDialog";
import {ShareCopyButton, useCopyFeedback} from "home/components/shareLink";
import {Actions, CardMain, CardText, DangerButton, SaveButton} from "../components/AccountCards";
import {useAccountSubdomain} from "../hooks/useAccountSubdomain";
import {MAX_LABEL_LENGTH} from "./subdomainClaim";
import {
	ClaimField,
	ErrorText,
	FieldLabel,
	FieldSuffix,
	Hint,
	HostRow,
	HostValue,
	LabelInput,
	LockedNote,
	Row,
} from "./SubdomainSettings.styles";

/**
 * The claimed-subdomain editor (kitecorp/transferify-api-service#350), with NO surface of its own — the
 * caller supplies the frame and the heading, exactly as {@code BrandSettings} does.
 *
 * Its one caller is {@link BrandSheet}, as the "Your address" section of the branding sheet. It had an
 * account → Profile card of its own until ui#529, when the owner put every brand-identity editor in that
 * one sheet; there is no second surface for it to drift from.
 *
 * Presentational only: the entitlement gate, the label rule and the three `SubdomainClaimService` calls live
 * in {@link useAccountSubdomain} and `subdomainClaim.ts` (epic #26).
 *
 * Three things here are contract, not styling:
 * 1. A non-entitled sender sees no claim FORM, never a disabled-looking one — the api rejects their claim
 *    anyway, so exposing the control would only produce 400s.
 * 2. A non-entitled sender who still HOLDS a claim nevertheless sees it, and can release it (ui#534). That
 *    is the api's stated contract — `current` and `release` take `requireAccount`, only `claim` takes
 *    `requireEntitled` — and the ui contradicting it left a name reserved against every other account with
 *    nobody able to give it back. The `lapsed` panel is deliberately NOT the `claimed` one: a downgraded
 *    owner's addresses have stopped serving, so it says so instead of listing them under "Your links live on".
 * 3. The claimed addresses are rendered from the SERVER's `hosts`, never composed from a suffix in the
 *    client: the brand hosts differ per environment, and a hardcoded `.transferify.eu` would show dev
 *    users an address that is not theirs. Since ui#725 the hook FILTERS that list to the brand the sender
 *    is browsing — still the server's strings, just not all of them — so a sender on transferify.ro is not
 *    handed the main brand's address. The claim still serves on both zones; only the advertising is scoped.
 */
export type SubdomainSettingsProps = {
	/**
	 * Whether THIS section renders the sheet's single "Available on Premium." note. The sheet decides
	 * ({@link brandSheetUpsell}) because its sections share that one sentence: a section that answered
	 * for itself would put it on screen twice for a FREE sender, which is both noise and a Playwright
	 * strict-mode failure. Locked with `false` simply renders nothing — the note is elsewhere in the
	 * same sheet.
	 */
	showLockedNote: boolean;
};

export default function SubdomainSettings({showLockedNote}: SubdomainSettingsProps) {
	const m = useMessages();
	const {state, claim, hosts, label, suffix, busy, error, confirmOpen, setLabel, submit, openConfirm,
		closeConfirm, release} = useAccountSubdomain();

	// The two states that render a held claim share every control; only the heading and the sentence under
	// the addresses differ, because one describes a live address and the other one that has stopped serving.
	const lapsed = state === "lapsed";
	const held = lapsed || state === "claimed";

	return (
		<>
			<CardText>{m.account.subdomain.description}</CardText>

			{(state === "locked" || lapsed) && showLockedNote && (
				<LockedNote>
					<ProPill aria-label={m.account.subdomain.lockedAria}>{m.pricing.planNames.premium}</ProPill>
					<Hint>{m.account.subdomain.lockedHint}</Hint>
				</LockedNote>
			)}

			{state === "loading" && <Hint>{m.account.subdomain.loading}</Hint>}

			{state === "unclaimed" && (
				<CardMain>
					<Row>
						<FieldLabel htmlFor="subdomain-label">{m.account.subdomain.labelLabel}</FieldLabel>
					</Row>
					<Row>
						<ClaimField>
							<LabelInput
								id="subdomain-label"
								value={label}
								disabled={busy}
								maxLength={MAX_LABEL_LENGTH}
								placeholder={m.account.subdomain.labelPlaceholder}
								autoCapitalize="none"
								autoCorrect="off"
								spellCheck={false}
								aria-describedby="subdomain-hint"
								onChange={(event) => setLabel(event.target.value)}
								onKeyDown={(event) => {
									if (event.key === "Enter") {
										event.preventDefault();
										void submit();
									}
								}}
							/>
							<FieldSuffix>{suffix}</FieldSuffix>
						</ClaimField>
						<SaveButton disabled={busy} onClick={() => void submit()}>
							{m.account.subdomain.claim}
						</SaveButton>
					</Row>
					<Hint id="subdomain-hint">{m.account.subdomain.labelHint}</Hint>
					{error && <ErrorText role="alert">{error}</ErrorText>}
				</CardMain>
			)}

			{held && claim && (
				<CardMain>
					<Row>
						<FieldLabel as="span">
							{lapsed ? m.account.subdomain.lapsedTitle : m.account.subdomain.claimedTitle}
						</FieldLabel>
					</Row>
					{hosts.map((host) => (
						<CopyableHost key={host} host={host}/>
					))}
					<Hint>{lapsed ? m.account.subdomain.lapsedNote : m.account.subdomain.snapshotNote}</Hint>
					{error && <ErrorText role="alert">{error}</ErrorText>}
					<Actions>
						<DangerButton disabled={busy} onClick={openConfirm}>
							{m.account.subdomain.release}
						</DangerButton>
					</Actions>
					<ConfirmDialog
						open={confirmOpen}
						title={m.account.subdomain.releaseTitle}
						body={<ConfirmBody><strong>{claim.label}</strong> — {m.account.subdomain.releaseBody}</ConfirmBody>}
						cancelLabel={m.common.cancel}
						confirmLabel={m.account.subdomain.releaseConfirm}
						onCancel={closeConfirm}
						onConfirm={() => void release()}
					/>
				</CardMain>
			)}
		</>
	);
}

/** One claimed origin with the shared "Copy / ✓ Copied" action. */
function CopyableHost({host}: {host: string}) {
	const m = useMessages();
	const {copied, copy} = useCopyFeedback(host);
	return (
		<HostRow>
			<HostValue>{host}</HostValue>
			<ShareCopyButton type="button" $ok={copied} aria-label={m.account.subdomain.copyAria} onClick={copy}>
				{copied ? `✓ ${m.common.copied}` : m.common.copy}
			</ShareCopyButton>
		</HostRow>
	);
}
